import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, UserCircle, X } from "lucide-react";
import { useAuth } from "../AuthContext";
import NotificationBell from "./NotificationBell";
import { useLang } from "../LangContext";
import { useInterMappaOptional } from "../context/InterMappaContext";
import LangSwitcher from "./LangSwitcher";
import HeaderMobileQuickActions from "./HeaderMobileQuickActions";
import OfflineBadge from "./OfflineBadge";
import BrandLogo from "./BrandLogo";
import { performHomeReset } from "../utils/homeReset";
import "../site-header-6amico.css";
import "../header-mobile-quick.css";

const DROPDOWN_PANEL =
  "absolute top-full left-0 z-50 flex flex-col bg-white shadow-lg border border-gray-200 rounded-md p-2 w-64";

/** Voci secondarie — solo dropdown «Network» */
const NETWORK_DROPDOWN_LINKS = [
  { to: "/gemellaggi", label: "Gemellaggi", labelKey: "twinnings" },
  { to: "/vetrina", label: "Vetrina", labelKey: "merchants" },
  { to: "/comuni-amici", label: "Comuni Amici" },
  { to: "/mondo", label: "Mondo", labelKey: "world" },
];

function HashNavLink({ hash, children, className = "", onNavigate }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const id = hash.replace(/^#/, "");

  const go = (e) => {
    e?.preventDefault();
    onNavigate?.();
    if (pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", hash);
    } else {
      navigate(`/${hash}`);
    }
  };

  return (
    <a href={hash} className={className} onClick={go}>
      {children}
    </a>
  );
}

function HomeResetLink({ children, className = "", onNavigate, testId, ariaLabel }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const interMappa = useInterMappaOptional();

  const go = (e) => {
    onNavigate?.();
    performHomeReset(interMappa);
    if (pathname !== "/") return;
    e.preventDefault();
    navigate("/", { replace: true });
  };

  return (
    <Link to="/" className={className} onClick={go} data-testid={testId} aria-label={ariaLabel}>
      {children}
    </Link>
  );
}

function NavDropdown({ label, testId, items, onNavigate, variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isDesktop = variant === "desktop";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = () => {
    setOpen(false);
    onNavigate?.();
  };

  const panelVisible = open ? "flex" : "hidden";

  return (
    <li
      ref={ref}
      className={
        isDesktop
          ? `nav-dropdown relative shrink-0 ${open ? "is-open" : ""}`
          : `nav-dropdown nav-dropdown--mobile ${open ? "is-open" : ""}`
      }
      onMouseEnter={isDesktop ? () => setOpen(true) : undefined}
      onMouseLeave={isDesktop ? () => setOpen(false) : undefined}
    >
      <button
        type="button"
        className="btn-enti-toggle inline-flex items-center gap-1 whitespace-nowrap"
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown
          size={14}
          className={`opacity-70 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      <ul
        role="menu"
        className={
          isDesktop
            ? `${DROPDOWN_PANEL} nav-dropdown-panel ${panelVisible} mt-1`
            : `nav-dropdown-panel nav-dropdown-panel--mobile ${panelVisible} flex-col gap-1 pl-3 mt-2 border-l-2 border-gray-200`
        }
      >
        {items.map((item) => (
          <li key={item.to} role="none" className="block w-full">
            {item.hash ? (
              <HashNavLink
                hash={item.to.replace(/^\//, "")}
                className="nav-dropdown-link block w-full rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0066cc]"
                onNavigate={close}
              >
                {item.label}
              </HashNavLink>
            ) : (
              <Link
                to={item.to}
                role="menuitem"
                className="nav-dropdown-link block w-full rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0066cc]"
                onClick={close}
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

function SiteLogo({ onNavigate }) {
  return (
    <HomeResetLink
      className="logo-link brand-logo-home-link"
      testId="logo-home-link"
      ariaLabel="6amico — torna alla home"
      onNavigate={onNavigate}
    >
      <BrandLogo variant="header" className="logo-vettore transition-transform duration-200" />
    </HomeResetLink>
  );
}

function buildNavItems({ t, user, loginTo, loginLabel, closeMobile, onLogout, variant }) {
  const networkItems = NETWORK_DROPDOWN_LINKS.map((item) => ({
    ...item,
    label: item.labelKey ? t(`nav.${item.labelKey}`) || item.label : item.label,
  }));

  const piazzeLabel = t("nav.piazze") || "Piazza Social";

  return (
    <>
      <li className="shrink-0">
        <Link to="/piazze" onClick={closeMobile} className="whitespace-nowrap" data-testid="nav-piazze">
          {piazzeLabel}
        </Link>
      </li>
      <li className="shrink-0">
        <HashNavLink hash="#esplora" className="whitespace-nowrap" onNavigate={closeMobile}>
          {t("nav.explore") || "Esplora Borghi"}
        </HashNavLink>
      </li>
      <li className="shrink-0">
        <Link to="/eventi" onClick={closeMobile} className="whitespace-nowrap" data-testid="nav-eventi">
          {t("nav.events") || "Eventi & Sagre"}
        </Link>
      </li>
      <NavDropdown
        label={t("nav.network") || "Network"}
        testId="nav-dropdown-network"
        items={networkItems}
        onNavigate={closeMobile}
        variant={variant}
      />
      {/* Accesso / profilo: su desktop nella barra utilities; su mobile qui */}
      {!user && (
        <li className="shrink-0">
          <Link to={loginTo} className="btn-custode whitespace-nowrap" data-testid="nav-login-custode" onClick={closeMobile}>
            {loginLabel}
          </Link>
        </li>
      )}
      {user && variant === "mobile" && (
        <>
          <li className="shrink-0">
            <Link to="/il-mio-spazio" onClick={closeMobile} className="whitespace-nowrap" data-testid="nav-mio-spazio-mobile">
              {loginLabel}
            </Link>
          </li>
          <li className="shrink-0">
            <Link to="/punti-merito" onClick={closeMobile} className="whitespace-nowrap" data-testid="nav-favorites-mobile">
              I miei preferiti
            </Link>
          </li>
          <li className="shrink-0">
            <button
              type="button"
              onClick={() => { onLogout?.(); closeMobile?.(); }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-ink2 hover:text-terracotta transition-colors"
              data-testid="nav-logout"
            >
              <LogOut size={14} aria-hidden /> Esci
            </button>
          </li>
        </>
      )}
    </>
  );
}

function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, langEpoch } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  const isGovernanceRoute = /^\/(governance|stanza-governance|mayors-council|stanza-sindaci)(\/|$)/.test(pathname);
  const loginTo = user ? "/il-mio-spazio" : `/login?return=${encodeURIComponent(pathname)}`;
  const loginLabel = user ? "Il mio spazio" : "Accedi / Registrati";

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (pathname !== "/" || !window.location.hash) return undefined;
    const id = window.location.hash.replace(/^#/, "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  if (isGovernanceRoute) {
    return (
      <header className="glass-header sticky top-0 z-50" data-testid="site-header-governance">
        <div className="max-w-[1200px] mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/governance" className="text-sm font-medium text-slate-300 hover:text-white">
            Governance 6AMICO
          </Link>
          <HomeResetLink className="text-xs text-slate-400 hover:text-white">← Sito pubblico</HomeResetLink>
        </div>
      </header>
    );
  }

  const closeMobile = () => setMobileOpen(false);
  const navProps = { t, user, loginTo, loginLabel, closeMobile, onLogout: handleLogout };

  const mobileDrawer =
    mobileOpen &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <button
          type="button"
          className="site-header-mobile-backdrop"
          aria-label="Chiudi menu"
          data-testid="mobile-nav-backdrop"
          onClick={closeMobile}
        />
        <nav
          className="site-header-global__mobile-panel sidebar-menu-links menu-laterale-sinistro is-open"
          aria-label="Menu mobile"
          data-testid="mobile-primary-nav"
        >
          <ul className="nav-menu-mobile">{buildNavItems({ ...navProps, variant: "mobile" })}</ul>
        </nav>
      </>,
      document.body,
    );

  return (
    <>
      <header className="header-6amico site-header-global" data-testid="site-header" data-lang-epoch={langEpoch}>
        <div className="header-container navbar-container-mobile">
          <SiteLogo onNavigate={closeMobile} />

          <nav className="nav-menu nav-menu--desktop" aria-label="Navigazione principale">
            <ul className="nav-menu-row">{buildNavItems({ ...navProps, variant: "desktop" })}</ul>
          </nav>

          <div className="site-header-global__utilities barra-superiore-icone">
            <OfflineBadge className="header-util--desktop-only hidden sm:inline-flex" />
            <HeaderMobileQuickActions />
            <LangSwitcher variant="header" />
            {user && (
              <Link
                to="/punti-merito"
                className="header-util--desktop-only hidden lg:inline-flex items-center rounded-full border border-borderline px-3 py-1.5 text-xs text-ink2 hover:text-terracotta hover:border-terracotta transition-colors"
                data-testid="header-favorites-link"
                title="I miei preferiti"
              >
                I miei preferiti
              </Link>
            )}
            {user && <NotificationBell />}
            {user && (
              <Link
                to="/il-mio-spazio"
                className="header-util--desktop-only profile-circle user-avatar hidden lg:inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0"
                data-testid="header-profile-link"
                title="Il mio spazio"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" width={40} height={40} />
                ) : (
                  <UserCircle size={22} aria-hidden />
                )}
              </Link>
            )}
            {user && (
              <button
                type="button"
                onClick={handleLogout}
                className="header-util--desktop-only hidden lg:inline-flex items-center gap-1.5 rounded-full border border-borderline px-3 py-1.5 text-xs text-ink2 hover:text-terracotta hover:border-terracotta transition-colors"
                data-testid="header-logout"
                title="Esci"
              >
                <LogOut size={14} aria-hidden /> Esci
              </button>
            )}
            <button
              type="button"
              className="site-header-global__menu-toggle"
              aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={mobileOpen}
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      {mobileDrawer}
    </>
  );
}

export default React.memo(Header);
